import { parseWorld, type World, type WorldManifest } from "@cimara/world";

export interface LoadedWorld {
  world: World;
  manifest: WorldManifest;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function fetchOk(url: string, signal?: AbortSignal): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, { signal, cache: "no-store" });
  } catch (error) {
    throw new Error(`Could not reach ${url}: ${errorMessage(error)}`);
  }
  if (!response.ok) {
    throw new Error(`${url} returned HTTP ${response.status}`);
  }
  return response;
}

/**
 * Load the authoritative world the server is serving. The manifest is fetched
 * first so a partial deployment fails with a readable error rather than a
 * misleading hash mismatch.
 */
export async function loadWorld(signal?: AbortSignal): Promise<LoadedWorld> {
  const manifestResponse = await fetchOk("/manifest.json", signal);
  let manifest: WorldManifest;
  try {
    manifest = (await manifestResponse.json()) as WorldManifest;
  } catch (error) {
    throw new Error(`manifest.json is not valid JSON: ${errorMessage(error)}`);
  }

  const worldResponse = await fetchOk("/world.bin", signal);
  const buffer = await worldResponse.arrayBuffer();

  try {
    const world = await parseWorld(buffer, manifest);
    return { world, manifest };
  } catch (error) {
    throw new Error(`The world file was rejected: ${errorMessage(error)}`);
  }
}
